import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Link,
  Flex,
  Typography,
  useThemedValueSwitch,
  Heading,
} from '@tq/ui-components';

import { Notes, NotesHeading, AddNoteButton, AddNote } from 'components';
import { Consultation } from 'types';
import { CaseDetailsCard, CaseOfficerDetailsCard } from '../..';

export type DesktopSummaryProps = {
  consultation?: Consultation;
  isLoading: boolean;
};

export const DesktopSummary: React.FC<DesktopSummaryProps> = ({ consultation, isLoading }) => {
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const { t } = useTranslation('summary_header');
  const { tvs } = useThemedValueSwitch();

  useEffect(() => {
    !isLoading &&
    consultation &&
    (consultation?.consultationType === 'RPC' || consultation?.isArchived)
      ? setShowNotes(false)
      : setShowNotes(true);
  }, [isLoading, consultation, setShowNotes]);

  return (
    <Flex flexDirection='row' justifyContent='space-between' gridGap={8}>
      <Flex grow={1} flexDirection='column'>
        <Flex
          mb={tvs({ tq: '1.875rem' }, 5)}
          mt={tvs({ tq: 0 }, 3)}
          justifyContent='space-between'
          alignItems='baseline'
          pr={{ base: 0, lg: 5 }}
        >
          <Typography>
            <Heading>{t('case_details', 'Case details')}</Heading>
          </Typography>
          {!showNotes &&
            !isLoading &&
            consultation &&
            consultation?.consultationType !== 'RPC' && (
              <Link fontSize='sm' onClick={() => setShowNotes(true)}>
                {t('show_notes', 'Show notes')}
              </Link>
            )}
        </Flex>
        <CaseDetailsCard consultation={consultation} />
        <Typography mt={tvs({ tq: '2.125rem' }, 5)} mb={tvs({ tq: '1.375rem' }, 5)}>
          <Heading>{t('case_officer_details', 'Case officer details')}</Heading>
        </Typography>
        <CaseOfficerDetailsCard consultation={consultation} />
      </Flex>
      {showNotes && (
        <Box minWidth={420} maxWidth={420}>
          <Flex
            justifyContent='space-between'
            alignItems='baseline'
            pr={tvs({ tq: 0 }, { base: 0, lg: 5 })}
            mb={tvs({ tq: '1.375rem' }, 0)}
          >
            <NotesHeading consultation={consultation} />
            <AddNoteButton consultation={consultation} />
            {showNotes && consultation?.isArchived && (
              <Link fontSize='sm' onClick={() => setShowNotes(false)}>
                {t('hide', 'Hide')}
              </Link>
            )}
          </Flex>
          <Card p={tvs({ tq: '1.25rem' }, 4)} mt={tvs({ tq: 0 }, 6)}>
            <AddNote />
            <Notes notes={consultation?.notes} loading={isLoading} />
          </Card>
        </Box>
      )}
    </Flex>
  );
};
